import Loading from '@components/Loading';
import PreviewVideo from '@components/PreviewVideo';
import { css } from '@emotion/react';
import { useGetPath } from '@hooks/interview/useGetPath';
import { useInterviewSession } from '@hooks/interview/useInterviewSession';
import { useReferrerAPI } from '@hooks/referrer/useReferrerAPI';
import { useAttendee } from '@hooks/utils/useAttendee';
import { useInterval } from '@hooks/utils/useInterval';
import { useSocket } from '@hooks/utils/useSocket';
import { useLocalAudioOutput } from '@providers/LocalAudioOutputProvider';
import { useMeetingManager } from '@providers/MeetingProvider';
import { checkLogInterviewer } from '@stores/atoms/checkLog';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { SocketChannelKeys, SocketNamespaceKeys } from 'server/constants/key';
import { MeetingStatus } from 'server/interfaces/meeting.enum';
import {
  Check,
  Container,
  ContentDimmedWrapper,
  EnterRoom,
  Text,
  Title,
  VideoContainer,
} from './style';

const IntervieweeWaitingPage = () => {
  const { isAudioOn, toggleAudio } = useLocalAudioOutput();
  const { getInterviewInfo } = useInterviewSession();
  const { name, certificationCode, apiUrl } = getInterviewInfo();

  const { rootPath } = useGetPath();
  const navigate = useNavigate();
  const meetingManager = useMeetingManager();
  const setAlreadyEnteredInterviewer = useSetAtom(checkLogInterviewer);

  const socketClient = useSocket(SocketNamespaceKeys.INTERVIEWEE);
  const { fetchInterviewerLog, fetchIntervieweeCreateLog } = useReferrerAPI();

  const [step, setStep] = useState(0);
  const [count, setCount] = useState(0);

  const externalApiAddr = `https://${apiUrl}`;

  const moveNextStep = (next: number) => {
    if (next !== step) {
      setStep(next);
    }
  };

  const moveRoom = async () => {
    await meetingManager.start();
    navigate(rootPath.ROOM);
  };

  const enterMeetingRoom = () => {
    socketClient.emit(
      SocketChannelKeys.INIT,
      JSON.stringify({ certificationCode, status: MeetingStatus.START }),
    );
    socketClient.close();
    moveRoom();
  };

  const checkLogCreate = async () => {
    await fetchIntervieweeCreateLog({
      companyUrl: apiUrl,
      name,
      certificationCode,
      apiUrl: externalApiAddr,
    });
  };

  const isAlreadyEntered = async () => {
    const checkLog = await fetchInterviewerLog({
      companyUrl: apiUrl,
      name,
      certificationCode,
      apiUrl: externalApiAddr,
    });

    setAlreadyEnteredInterviewer(checkLog.data);
    return checkLog;
  };

  const goToRoomOrWait = async () => {
    socketClient.on(SocketChannelKeys.CONNECTION, async (socket) => {
      console.log('socket connected...');
    });

    const { data } = await isAlreadyEntered();

    if (data) {
      moveRoom();
    } else {
      await checkLogCreate();
      moveNextStep(1);

      socketClient.emit(
        SocketChannelKeys.INIT,
        JSON.stringify({
          certificationCode,
          status: MeetingStatus.INTERVIEWEE_READY,
        }),
      );

      socketClient.on(certificationCode, (data) => {
        const { status } = JSON.parse(data);
        // console.log('================================');
        // console.log(status);
        // console.log('================================');

        switch (status) {
          case MeetingStatus.INTERVIEWER_READY:
            return socketClient.emit(
              SocketChannelKeys.INIT,
              JSON.stringify({
                certificationCode,
                status: MeetingStatus.INTERVIEWEE_READY,
              }),
            );
          case MeetingStatus.ROOM_OPEN:
            return moveNextStep(2);
          default:
            return;
        }
      });
    }
  };

  // 평가자가 방을 열고 3분 후 자동 입장
  useInterval(async () => {
    if (step !== 2) return;

    if (count >= 180) {
      enterMeetingRoom();
    }

    setCount(count + 1);
  }, 1000);

  useAttendee();
  // useCameraOn();

  useEffect(() => {
    goToRoomOrWait();

    return () => {
      () => {
        socketClient.emit(
          SocketChannelKeys.END,
          JSON.stringify({ certificationCode, status: MeetingStatus.END }),
        );

        setTimeout(() => {
          socketClient.close();
        }, 300);
      };
    };
  }, []);

  useEffect(() => {
    // 대기실에서 응시자 스피커 음소거 처리
    if (!isAudioOn) return;
    toggleAudio();
  }, [isAudioOn, toggleAudio]);

  return (
    <Container>
      {step === 0 && <Loading />}
      {step === 1 && (
        <Loading
          title='면접관이 면접을 준비중입니다.'
          text={
            '면접관이 지원자의 대기실 입장을 확인하였습니다. 면접실의 면접 준비가 완료되면 [면접실 입장] 버튼이 활성화 됩니다.\n면접대기실에 입장하고 면접실의 면접준비가 완료되면 예정 시작시간 전에도 바로 면접이 시작될 수 있습니다.'
          }
        />
      )}
      {step === 2 && (
        <>
          <Check src='/img/check.png' />
          <Title>면접준비가 완료되었습니다.</Title>
          <Text>
            {`면접실 입장 버튼을 눌러주세요.\n3분내로 시작하지 않을 시, 자동으로 시작됩니다.`}
          </Text>
        </>
      )}
      {step > 0 && (
        <ContentDimmedWrapper
          css={css`
            opacity: ${step === 4 && '0.1'};
          `}
        >
          <VideoContainer>
            <PreviewVideo />
          </VideoContainer>
          <EnterRoom disabled={step === 1} onClick={enterMeetingRoom}>
            면접실 입장
          </EnterRoom>
        </ContentDimmedWrapper>
      )}
    </Container>
  );
};

export default IntervieweeWaitingPage;
